////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.1                                                            //
//  Summary: Label + control + error/hint wrapper — every form field uses this //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import type { ReactNode } from 'react';
import { useId } from 'react';

export interface FieldProps {
  label: string;
  error?: string;
  hint?: string;
  /** Render prop rather than children, so Field can hand the control the ids it
   * needs to wire up `htmlFor`/`aria-describedby`/`aria-invalid` itself — a control
   * cannot label or describe itself correctly without knowing them. */
  children: (ids: { inputId: string; describedBy: string | undefined }) => ReactNode;
}

export function Field ({ label, error, hint, children }: FieldProps) {
  const inputId = useId();
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-ink">
        {label}
      </label>
      {children({ inputId, describedBy })}
      {hint && (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
